import React from 'react';
import {
  Trophy,
  Medal,
  Target,
  MapPin,
  BarChart3,
  Heart,
  Lock,
  Star,
  Calendar,
  Clock,
  Users,
  Zap,
  TrendingUp,
  Award,
  Crown,
  Shield,
  Flag,
  Crosshair,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  ChevronsLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Plus,
  Minus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Upload,
  Settings,
  User,
  Mail,
  Phone,
  Eye,
  EyeOff,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Gem,
  Calculator,
  Lightbulb,
  DollarSign,
  Frown,
  QrCode,
  LogOut,
  Newspaper,
  CreditCard,
  Smartphone,
  Camera,
  MessageCircle,
  Copy,
  Rocket,
  Sparkles,
  Check,
  GraduationCap,
  Send,
  Shuffle,
  FileText,
  Eraser,
  Sun,
  Moon
} from 'lucide-react';

/**
 * Centralized Icon Component System
 * 
 * This component provides a consistent way to use icons across the application.
 * All icons are sourced from Lucide React for professional appearance.
 * 
 * Usage:
 * import Icons from '../components/Icons';
 * <Icons.Trophy className="w-6 h-6 text-yellow-500" />
 * 
 * To swap out an icon:
 * 1. Import a new icon from lucide-react at the top
 * 2. Replace the component in the Icons object below
 * 3. All usages across the app will automatically update
 */

const Icons = {
  // Awards & Recognition
  Trophy: ({ className = "w-6 h-6", ...props }) => <Trophy className={className} {...props} />,
  Medal: ({ className = "w-6 h-6", ...props }) => <Medal className={className} {...props} />,
  Award: ({ className = "w-6 h-6", ...props }) => <Award className={className} {...props} />,
  Crown: ({ className = "w-6 h-6", ...props }) => <Crown className={className} {...props} />,
  Star: ({ className = "w-6 h-6", ...props }) => <Star className={className} {...props} />,
  Shield: ({ className = "w-6 h-6", ...props }) => <Shield className={className} {...props} />,

  // Golf & Sports
  Golf: ({ className = "w-6 h-6", ...props }) => <Flag className={className} {...props} />,
  Target: ({ className = "w-5 h-5", ...props }) => <Target className={className} {...props} />,
  Crosshair: ({ className = "w-5 h-5", ...props }) => <Crosshair className={className} {...props} />,
  Flag: ({ className = "w-5 h-5", ...props }) => <Flag className={className} {...props} />,

  // Data & Analytics
  Chart: ({ className = "w-5 h-5", ...props }) => <BarChart3 className={className} {...props} />,
  TrendingUp: ({ className = "w-5 h-5", ...props }) => <TrendingUp className={className} {...props} />,

  // Location & Navigation
  Location: ({ className = "w-5 h-5", ...props }) => <MapPin className={className} {...props} />,
  ArrowLeft: ({ className = "w-5 h-5", ...props }) => <ArrowLeft className={className} {...props} />,
  ArrowUp: ({ className = "w-5 h-5", ...props }) => <ArrowUp className={className} {...props} />,
  ArrowDown: ({ className = "w-5 h-5", ...props }) => <ArrowDown className={className} {...props} />,
  ArrowRight: ({ className = "w-5 h-5", ...props }) => <ArrowRight className={className} {...props} />,
  ArrowDoubleLeft: ({ className = "w-5 h-5", ...props }) => <ChevronsLeft className={className} {...props} />,
  ChevronLeft: ({ className = "w-5 h-5", ...props }) => <ChevronLeft className={className} {...props} />,
  ChevronRight: ({ className = "w-5 h-5", ...props }) => <ChevronRight className={className} {...props} />,
  ChevronUp: ({ className = "w-5 h-5", ...props }) => <ChevronUp className={className} {...props} />,
  ChevronDown: ({ className = "w-5 h-5", ...props }) => <ChevronDown className={className} {...props} />,

  // Time & Calendar
  Calendar: ({ className = "w-5 h-5", ...props }) => <Calendar className={className} {...props} />,
  Clock: ({ className = "w-5 h-5", ...props }) => <Clock className={className} {...props} />,

  // User & Social
  Users: ({ className = "w-5 h-5", ...props }) => <Users className={className} {...props} />,
  User: ({ className = "w-5 h-5", ...props }) => <User className={className} {...props} />,
  Heart: ({ className = "w-5 h-5", ...props }) => <Heart className={className} {...props} />,
  Frown: ({ className = "w-5 h-5", ...props }) => <Frown className={className} {...props} />,

  // Security & Auth
  Lock: ({ className = "w-5 h-5", ...props }) => <Lock className={className} {...props} />,
  Eye: ({ className = "w-5 h-5", ...props }) => <Eye className={className} {...props} />,
  EyeOff: ({ className = "w-5 h-5", ...props }) => <EyeOff className={className} {...props} />,

  // Status & Feedback
  CheckCircle: ({ className = "w-5 h-5", ...props }) => <CheckCircle className={className} {...props} />,
  XCircle: ({ className = "w-5 h-5", ...props }) => <XCircle className={className} {...props} />,
  AlertTriangle: ({ className = "w-5 h-5", ...props }) => <AlertTriangle className={className} {...props} />,
  Warning: ({ className = "w-5 h-5", ...props }) => <AlertTriangle className={className} {...props} />,
  Info: ({ className = "w-5 h-5", ...props }) => <Info className={className} {...props} />,
  Zap: ({ className = "w-5 h-5", ...props }) => <Zap className={className} {...props} />,

  // Actions & Controls
  Plus: ({ className = "w-5 h-5", ...props }) => <Plus className={className} {...props} />,
  Minus: ({ className = "w-5 h-5", ...props }) => <Minus className={className} {...props} />,
  Edit: ({ className = "w-5 h-5", ...props }) => <Edit className={className} {...props} />,
  Trash: ({ className = "w-5 h-5", ...props }) => <Trash2 className={className} {...props} />,
  Search: ({ className = "w-5 h-5", ...props }) => <Search className={className} {...props} />,
  Filter: ({ className = "w-5 h-5", ...props }) => <Filter className={className} {...props} />,
  Download: ({ className = "w-5 h-5", ...props }) => <Download className={className} {...props} />,
  Upload: ({ className = "w-5 h-5", ...props }) => <Upload className={className} {...props} />,
  Settings: ({ className = "w-5 h-5", ...props }) => <Settings className={className} {...props} />,
  Copy: ({ className = "w-5 h-5", ...props }) => <Copy className={className} {...props} />,

  // Communication
  Mail: ({ className = "w-5 h-5", ...props }) => <Mail className={className} {...props} />,
  Phone: ({ className = "w-5 h-5", ...props }) => <Phone className={className} {...props} />,
  MessageCircle: ({ className = "w-5 h-5", ...props }) => <MessageCircle className={className} {...props} />,
  Send: ({ className = "w-5 h-5", ...props }) => <Send className={className} {...props} />,

  // Interface
  Menu: ({ className = "w-5 h-5", ...props }) => <Menu className={className} {...props} />,
  Close: ({ className = "w-5 h-5", ...props }) => <X className={className} {...props} />,
  X: ({ className = "w-5 h-5", ...props }) => <X className={className} {...props} />,

  // Fantasy Golf Specific Icons
  Crystal: ({ className = "w-5 h-5", ...props }) => <Gem className={className} {...props} />,
  Calculator: ({ className = "w-5 h-5", ...props }) => <Calculator className={className} {...props} />,
  Lightbulb: ({ className = "w-5 h-5", ...props }) => <Lightbulb className={className} {...props} />,
  Dollar: ({ className = "w-5 h-5", ...props }) => <DollarSign className={className} {...props} />,
  QRCode: ({ className = "w-5 h-5", ...props }) => <QrCode className={className} {...props} />,
  PayPal: ({ className = "w-5 h-5", ...props }) => <DollarSign className={className} {...props} />, // Using DollarSign as PayPal alternative
  Venmo: ({ className = "w-5 h-5", ...props }) => <Smartphone className={className} {...props} />, // Using Smartphone for Venmo
  Zelle: ({ className = "w-5 h-5", ...props }) => <CreditCard className={className} {...props} />, // Using CreditCard for Zelle

  // Authentication & Actions
  Logout: ({ className = "w-5 h-5", ...props }) => <LogOut className={className} {...props} />,

  // Content & Media
  News: ({ className = "w-5 h-5", ...props }) => <Newspaper className={className} {...props} />,
  Camera: ({ className = "w-5 h-5", ...props }) => <Camera className={className} {...props} />,

  // Missing Icons (added to fix undefined references)
  Success: ({ className = "w-5 h-5", ...props }) => <CheckCircle className={className} {...props} />,
  Error: ({ className = "w-5 h-5", ...props }) => <XCircle className={className} {...props} />,
  Rocket: ({ className = "w-5 h-5", ...props }) => <Rocket className={className} {...props} />,
  Sparkle: ({ className = "w-5 h-5", ...props }) => <Sparkles className={className} {...props} />,
  Check: ({ className = "w-5 h-5", ...props }) => <Check className={className} {...props} />,
  Cap: ({ className = "w-5 h-5", ...props }) => <GraduationCap className={className} {...props} />,

  // Event Management Icons
  Shuffle: ({ className = "w-5 h-5", ...props }) => <Shuffle className={className} {...props} />,
  FileText: ({ className = "w-5 h-5", ...props }) => <FileText className={className} {...props} />,
  Eraser: ({ className = "w-5 h-5", ...props }) => <Eraser className={className} {...props} />,
  Sun: ({ className = "w-5 h-5", ...props }) => <Sun className={className} {...props} />,
  Moon: ({ className = "w-5 h-5", ...props }) => <Moon className={className} {...props} />
};

/**
 * Icon wrapper with default props and consistent sizing
 * @param {string} name - Icon name from the Icons object
 * @param {number} size - Icon size (default: 20)
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional props to pass to the icon
 */
export const Icon = ({ name, size = 20, className = '', ...props }) => {
  const IconComponent = Icons[name];
  
  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in Icons component`);
    return null;
  }
  
  return (
    <IconComponent 
      size={size} 
      className={`inline-block ${className}`} 
      {...props} 
    />
  );
};

export default Icons;
